import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaRegCopy, FaRegFrownOpen, FaUserGraduate, FaTrash } from "react-icons/fa";
import { FaRegNoteSticky } from "react-icons/fa6";
import { MdNotifications, MdClose, MdEdit } from "react-icons/md";
import { FaBold, FaItalic, FaUnderline, FaUserPlus } from "react-icons/fa";
import { IoMdSettings } from "react-icons/io";
import { RiLogoutBoxFill } from "react-icons/ri";
import EditClassModal from "./editclassmodal";
import axios from "axios";
import { BASELINE } from "../../util";
import { toast } from "react-hot-toast";
import AddStudentModal from "./AddStudentModal";
import { motion, AnimatePresence } from "framer-motion";

// ── SAT domain → category structure ─────────────────────────────────────────
const SAT_DOMAINS = {
  eng: [
    {
      domain: 'Information and Ideas',
      categories: ['Inferences', 'Central Ideas and Details', 'Command of Evidence'],
    },
    {
      domain: 'Craft and Structure',
      categories: ['Words in Context', 'Text Structure and Purpose', 'Cross-Text Connections'],
    },
    {
      domain: 'Expression of Ideas',
      categories: ['Rhetorical Synthesis', 'Transitions'],
    },
    {
      domain: 'Standard English Conventions',
      categories: ['Boundaries', 'Form, Structure, and Sense'],
    },
  ],
  math: [
    {
      domain: 'Algebra',
      categories: [
        'Linear functions',
        'Systems of two linear equations in two variables',
        'Linear inequalities in one or two variables',
        'Linear equations in one variable',
        'Linear equations in two variables',
      ],
    },
    {
      domain: 'Advanced Math',
      categories: [
        'Nonlinear functions',
        'Equivalent expressions',
        'Nonlinear equations in one variable and systems of equations in two variables',
      ],
    },
    {
      domain: 'Problem-Solving & Data Analysis',
      categories: [
        'Ratios, rates, proportional relationships, and units',
        'Inference from sample statistics and margin of error',
        'Probability and conditional probability',
        'Percentages',
        'One-variable data: Distributions and measures of center and spread',
        'Two-variable data: Models and scatterplots',
        'Evaluating statistical claims: Observational studies and experiments',
      ],
    },
    {
      domain: 'Geometry & Trigonometry',
      categories: ['Lines, angles, and triangles', 'Right triangles and trigonometry', 'Area and volume', 'Circles'],
    },
  ],
};

const SUBJECTS_LIST = [
  { key: 'SAT',   label: 'Digital SAT', active: true  },
  { key: 'SAT89', label: 'SAT 8&9',     active: false },
  { key: 'NMSQT', label: 'NMSQT',       active: false },
  { key: 'ACT',   label: 'Digital ACT', active: false },
  { key: 'APCAB', label: 'AP Calc AB',  active: false },
  { key: 'APCBC', label: 'AP Calc BC',  active: false },
];

const clrLine = (pct) => pct >= 70 ? '#4ade80' : pct >= 40 ? '#facc15' : '#f87171';
const clrBg   = (pct) => pct >= 70 ? 'rgba(74,222,128,0.10)' : pct >= 40 ? 'rgba(250,204,21,0.08)' : 'rgba(248,113,113,0.10)';

// ── Main Component ───────────────────────────────────────────────────────────
function MyClassManagement({
  selectedClass, setSelectedClass, classlst, handleSelectChange,
  handleRemoveClass, handleCopyClassId, currentStudents, table,
  studentsPerPage, paginate, currentPage, handleRemove, handleViewDashboard,
  setsection, sethelpsession, setSelectedStudentId, setIsModalOpen,
  filteredLength, currentQuizzes, quizzesPerPage, paginate2, currentPage2,
  setsavedquizmodel, setquiztitle, setquizid, setquizdata,
  ensureQuizDataStructure, setquizgroup, setquiz_type, get_quiz_view,
  handleNavigate, user, setquizview, fetchClassList, updateStudentList,
  filteredQuizzes,
}) {
  const bgUrl = `${process.env.PUBLIC_URL}/bgimg${parseInt(selectedClass.bg) || 1}.png`;
  const levelLabel = (l) => l == 1 ? 'Beginner' : l == 2 ? 'Intermediate' : l == 3 ? 'Advanced' : '';

  // ── existing state ──────────────────────────────────────────────────────────
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [currentClassData, setCurrentClassData] = useState(null);
  const [isAddStudentOpen, setIsAddStudentOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showAddNotification, setShowAddNotification] = useState(false);
  const [newNotification, setNewNotification] = useState({
    title: '', content: '', date: new Date().toISOString().split('T')[0],
  });
  const [isEditingNotification, setIsEditingNotification] = useState(false);
  const [currentNotificationId, setCurrentNotificationId] = useState(null);
  const editorRef = useRef(null);
  const navigate = useNavigate();

  // ── class overview state ───────────────────────────────────────────────────
  const [overviewSubject,  setOverviewSubject]  = useState('SAT');
  const [overviewSection,  setOverviewSection]  = useState('eng');
  const [overviewData,     setOverviewData]     = useState({});
  const [overviewLoading,  setOverviewLoading]  = useState(false);

  // ── fetch overview when class or quizzes change ────────────────────────────
  useEffect(() => { fetchClassOverview(); }, [selectedClass?.classid, filteredQuizzes?.length]);

  const fetchClassOverview = async () => {
    if (!selectedClass?.classid || !filteredQuizzes?.length) { setOverviewData({}); return; }
    setOverviewLoading(true);
    try {
      const arrays = await Promise.all(
        filteredQuizzes.map(quiz =>
          axios.post(`${BASELINE}tests/getstudents`, [quiz.testid])
            .then(r => (r.data.testId || []).filter(item => item.classid === selectedClass.classid))
            .catch(() => [])
        )
      );
      const allResults = arrays.flat();
      const stats = {};
      allResults.forEach(({ testresult }) => {
        const process = (idsJson, isCorrect) => {
          try {
            JSON.parse(idsJson || '[]').forEach(q => {
              if (!q?.topic) return;
              const key = `${q.tag}||${q.topic}`;
              if (!stats[key]) stats[key] = { tag: q.tag, topic: q.topic, correct: 0, total: 0 };
              stats[key].total++;
              if (isCorrect) stats[key].correct++;
            });
          } catch {}
        };
        process(testresult.Correct_questionIds, true);
        process(testresult.Incorrect_questionIds, false);
      });
      setOverviewData(stats);
    } catch (e) { console.error(e); }
    setOverviewLoading(false);
  };

  // ── existing notification/class functions (unchanged) ─────────────────────
  useEffect(() => { if (selectedClass?.classid) fetchNotifications(); }, [selectedClass]);
  useEffect(() => {
    const esc = (e) => { if (e.keyCode === 27) setShowAddNotification(false); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, []);
  useEffect(() => {
    if (showAddNotification) {
      setTimeout(() => {
        if (!editorRef.current) return;
        editorRef.current.innerHTML = '';
        if (isEditingNotification && newNotification.content)
          editorRef.current.innerHTML = newNotification.content;
        editorRef.current.focus();
      }, 100);
    }
  }, [showAddNotification]);

  const fetchNotifications = async () => {
    try {
      const r = await axios.get(`${BASELINE}notification/get/${selectedClass.classid}`);
      if (r.status === 200) setNotifications(r.data);
    } catch { toast.error('Failed to load notifications'); }
  };

  const handleEditorChange = () => {
    if (!editorRef.current) return;
    setNewNotification(prev => ({ ...prev, content: editorRef.current.innerHTML }));
  };
  const handleFormatText = (cmd) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(cmd, false, null);
    setNewNotification(prev => ({ ...prev, content: editorRef.current.innerHTML }));
  };

  const handleAddNotification = async () => {
    if (!newNotification.title.trim() || !editorRef.current?.textContent.trim()) {
      toast.error('Title and content are required'); return;
    }
    try {
      let response;
      if (isEditingNotification) {
        if (!currentNotificationId) { toast.error('Cannot update: Missing ID'); return; }
        response = await axios.put(`${BASELINE}notification/update`, {
          id: currentNotificationId, title: newNotification.title,
          context: newNotification.content, date: newNotification.date || new Date().toISOString().split('T')[0],
        });
      } else {
        response = await axios.post(`${BASELINE}notification/create`, {
          classid: selectedClass.classid, owner: user.u_id,
          date: newNotification.date || new Date().toISOString().split('T')[0],
          title: newNotification.title, context: newNotification.content,
        });
      }
      if (response.status === 200) {
        fetchNotifications();
        setNewNotification({ title: '', content: '', date: new Date().toISOString().split('T')[0] });
        if (editorRef.current) editorRef.current.innerHTML = '';
        setShowAddNotification(false);
        setIsEditingNotification(false);
        setCurrentNotificationId(null);
        toast.success(isEditingNotification ? 'Notification updated' : 'Notification added');
      }
    } catch (err) { toast.error(`Failed to save: ${err.response?.data?.message || err.message}`); }
  };

  const handleEditNotification = (n) => {
    setIsEditingNotification(true);
    setCurrentNotificationId(n.id);
    setNewNotification({
      title: n.title, content: n.context,
      date: n.date ? new Date(n.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    });
    setShowAddNotification(true);
  };

  const handleDeleteNotification = async (id) => {
    if (!window.confirm('Delete this notification?')) return;
    try {
      const r = await axios.delete(`${BASELINE}notification/delete/${id}`);
      if (r.status === 200) { setNotifications(prev => prev.filter(n => n.id !== id)); toast.success('Deleted'); }
    } catch (err) { toast.error(`Failed to delete: ${err.response?.data?.message || err.message}`); }
  };

  async function handleEditClass(updatedClassData) {
    try {
      const response = await axios.post(BASELINE + 'class/update', [updatedClassData]);
      if (response.status === 200) {
        await fetchClassList();
        toast.success('Class updated successfully!');
        setSelectedClass(prev => ({
          ...prev,
          class_name: updatedClassData.class_name,
          typeclass: updatedClassData.type,
          level: updatedClassData.level,
          bg: updatedClassData.bg,
        }));
      }
    } catch { toast.error('Error updating class. Please try again.'); }
  }

  const refreshStudents = async () => {
    try {
      const res = await axios.post(BASELINE + 'getclassbyid', [{ classid: selectedClass.classid }]);
      updateStudentList(res.data ?? []);
    } catch { toast.error('Failed to refresh student list.'); }
  };

  const handleRemoveStudent = async (studentId) => {
    if (!window.confirm('Are you sure you want to remove this student?')) return;
    try {
      const r = await axios.post(BASELINE + 'classdelstudent', [{ classid: selectedClass.classid, studentid: studentId }]);
      if (r.status === 200) { toast.success(r.data.message || 'Student removed.'); await refreshStudents(); }
    } catch { toast.error('Failed to remove student.'); }
  };

  const handleAddStudent = async (studentId) => {
    try {
      const r = await axios.post(BASELINE + 'classaddstudent', [{
        classid: selectedClass.classid, studentid: studentId,
        shortid: selectedClass.shortid, owner: user.u_id,
      }]);
      if (r.status === 200) { toast.success(r.data.message || 'Student added!'); await refreshStudents(); setIsAddStudentOpen(false); }
    } catch { toast.error('Failed to add student.'); }
  };

  const handlePrint = (test) => {
    navigate(`/main/teacher/${selectedClass.classid}/pdf/${test.testid}`, { state: { test } });
  };

  // ── small icon button ──────────────────────────────────────────────────────
  const IconBtn = ({ title, onClick, children, danger }) => (
    <button title={title} onClick={onClick}
      className="p-2.5 rounded-xl transition-all"
      style={{
        background: danger ? 'rgba(239,68,68,0.12)' : 'rgba(255,255,255,0.07)',
        border: danger ? '1px solid rgba(239,68,68,0.20)' : '1px solid rgba(255,255,255,0.10)',
        color: danger ? 'rgba(239,68,68,0.70)' : 'rgba(255,255,255,0.55)',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = danger ? 'rgba(239,68,68,0.24)' : 'rgba(255,255,255,0.14)'; e.currentTarget.style.color = danger ? '#f87171' : 'white'; }}
      onMouseLeave={e => { e.currentTarget.style.background = danger ? 'rgba(239,68,68,0.12)' : 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = danger ? 'rgba(239,68,68,0.70)' : 'rgba(255,255,255,0.55)'; }}>
      {children}
    </button>
  );

  // ── Class Overview render ──────────────────────────────────────────────────
  const renderOverview = () => {
    const domains = SAT_DOMAINS[overviewSection] || [];
    const hasData = Object.keys(overviewData).length > 0;

    // compute weakest 3 for the summary strip
    const allCats = domains.flatMap(d => d.categories).map(cat => {
      const key = `${overviewSection}||${cat}`;
      const s = overviewData[key];
      return s?.total > 0 ? { cat, pct: Math.round((s.correct / s.total) * 100) } : null;
    }).filter(Boolean).sort((a, b) => a.pct - b.pct);
    const weakest = allCats.slice(0, 3);

    return (
      <div className="flex flex-col h-full">
        {/* Subject selector + Eng/Math tabs — same row */}
        <div className="flex items-center gap-3 mb-4">
          <label className="text-xs font-semibold uppercase tracking-wider flex-shrink-0" style={{ color: 'rgba(255,255,255,0.35)' }}>
            Subject
          </label>
          <div className="relative flex-shrink-0">
            <select
              value={overviewSubject}
              onChange={e => setOverviewSubject(e.target.value)}
              className="appearance-none text-sm font-semibold rounded-xl pl-3 pr-8 py-1.5 outline-none transition-all cursor-pointer"
              style={{
                background: 'rgba(99,102,241,0.15)',
                border: '1px solid rgba(99,102,241,0.35)',
                color: '#a5b4fc',
                colorScheme: 'dark',
              }}
            >
              {SUBJECTS_LIST.map(({ key, label, active }) => (
                <option key={key} value={key} disabled={!active}
                  style={{ background: '#1e2130', color: active ? '#e2e8f0' : '#4b5563' }}>
                  {label}{!active ? ' (coming soon)' : ''}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px]" style={{ color: '#a5b4fc' }}>▾</span>
          </div>

          <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            {[{ id: 'eng', label: 'English' }, { id: 'math', label: 'Math' }].map(({ id, label }) => (
              <button key={id} onClick={() => setOverviewSection(id)}
                className="px-4 py-1.5 rounded-lg text-sm font-semibold transition-all"
                style={overviewSection === id
                  ? { background: 'rgba(99,102,241,0.25)', border: '1px solid rgba(99,102,241,0.35)', color: '#a5b4fc' }
                  : { background: 'transparent', color: 'rgba(255,255,255,0.40)', border: '1px solid transparent' }}>
                {label}
              </button>
            ))}
          </div>
        </div>


        {/* Domain breakdown */}
        {overviewLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 rounded-xl animate-pulse" style={{ background: 'rgba(255,255,255,0.04)' }} />
            ))}
          </div>
        ) : !hasData ? (
          <div className="flex flex-col items-center justify-center flex-1 py-16 text-center">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
              style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.14)' }}>
              <FaRegNoteSticky size={20} style={{ color: 'rgba(165,180,252,0.45)' }} />
            </div>
            <p className="text-sm font-semibold mb-1" style={{ color: 'rgba(255,255,255,0.38)' }}>No data yet</p>
            <p className="text-xs max-w-xs" style={{ color: 'rgba(255,255,255,0.20)' }}>
              Assign homework and have students submit results to see class-wide performance.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 overflow-y-auto pr-1">
            {domains.map(({ domain, categories }) => {
              const catData = categories.map(cat => {
                const key = `${overviewSection}||${cat}`;
                const s = overviewData[key];
                const pct = s?.total > 0 ? Math.round((s.correct / s.total) * 100) : null;
                return { cat, pct, total: s?.total || 0, correct: s?.correct || 0 };
              });
              const withData = catData.filter(c => c.pct !== null);
              const domainAvg = withData.length > 0
                ? Math.round(withData.reduce((a, c) => a + c.pct, 0) / withData.length)
                : null;

              return (
                <div key={domain} className="rounded-xl overflow-hidden flex flex-col"
                  style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  {/* Domain header */}
                  <div className="flex items-center justify-between px-3 py-2"
                    style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <span className="text-[11px] font-bold uppercase tracking-wider truncate mr-2" style={{ color: 'rgba(255,255,255,0.50)' }}>
                      {domain}
                    </span>
                    {domainAvg !== null && (
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-md flex-shrink-0"
                        style={{ color: clrLine(domainAvg), background: clrBg(domainAvg) }}>
                        {domainAvg}%
                      </span>
                    )}
                  </div>
                  {/* Categories */}
                  <div className="px-3 py-2.5 space-y-2 flex-1">
                    {catData.map(({ cat, pct, total, correct }) => (
                      <div key={cat}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[11px] truncate mr-2" title={cat}
                            style={{ color: 'rgba(255,255,255,0.55)' }}>
                            {cat}
                          </span>
                          {pct !== null ? (
                            <span className="text-[11px] font-bold flex-shrink-0"
                              style={{ color: clrLine(pct) }}>
                              {pct}%
                            </span>
                          ) : (
                            <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.18)' }}>—</span>
                          )}
                        </div>
                        <div className="h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.07)' }}>
                          {pct !== null && (
                            <div className="h-full rounded-full transition-all duration-500"
                              style={{ width: `${pct}%`, background: clrLine(pct) }} />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="p-5 lg:p-7 space-y-5 min-h-screen">

        {/* ── Header ───────────────────────────────────────────────────────── */}
        <div className="relative rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.09)' }}>
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${bgUrl})` }} />
          <div className="absolute inset-0" style={{ background: 'rgba(8,10,18,0.80)', backdropFilter: 'blur(3px)' }} />
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-4 px-6 py-5">
            <div className="flex-1 min-w-0">
              <p className="text-white/35 text-xs mb-1 tracking-wide">
                <span className="cursor-pointer hover:text-white/60 transition-colors"
                  onClick={() => { setsection(9); setSelectedClass(''); }}>
                  Classes
                </span>
                {' › '}
                <span className="text-white/60">{selectedClass.class_name}</span>
              </p>
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-xl font-bold text-white">{selectedClass.class_name}</h1>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide"
                  style={{ background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.65)', border: '1px solid rgba(255,255,255,0.14)' }}>
                  {selectedClass.typeclass}
                </span>
                {selectedClass.level && (
                  <span className="text-sm" style={{ color: 'rgba(255,255,255,0.40)' }}>{levelLabel(selectedClass.level)}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <div>
                <p className="text-xs font-medium mb-0.5" style={{ color: 'rgba(255,255,255,0.30)' }}>Class Code</p>
                <p className="text-2xl font-bold text-white tracking-[0.18em]">{selectedClass.shortid}</p>
              </div>
              <button onClick={() => handleCopyClassId(selectedClass.shortid)}
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.55)' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = 'white'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; }}>
                <FaRegCopy size={13} /> Copy
              </button>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 sm:border-l sm:pl-5" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
              <IconBtn title="Add Student" onClick={() => setIsAddStudentOpen(true)}><FaUserPlus size={15} /></IconBtn>
              <IconBtn title="Edit class" onClick={() => { setCurrentClassData(selectedClass); setIsEditOpen(true); }}><IoMdSettings size={15} /></IconBtn>
              <IconBtn title="Back to classes" onClick={() => { setsection(9); setSelectedClass(''); }}><RiLogoutBoxFill size={15} /></IconBtn>
              <IconBtn title="Delete class" onClick={handleRemoveClass} danger><FaTrash size={13} /></IconBtn>
            </div>
          </div>
        </div>

        {/* ── Main grid: Overview (large) | Right sidebar ───────────────────── */}
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-5 items-start">

          {/* ── Class Overview ──────────────────────────────────────────────── */}
          <div className="rounded-2xl overflow-hidden"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center justify-between px-5 py-4"
              style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.60)' }}>Class Overview</span>
              </div>
              <button onClick={fetchClassOverview}
                className="text-xs px-2.5 py-1 rounded-lg transition-all"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)', color: 'rgba(255,255,255,0.40)' }}
                onMouseEnter={e => { e.currentTarget.style.color = 'white'; e.currentTarget.style.background = 'rgba(255,255,255,0.10)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.40)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}>
                ↻ Refresh
              </button>
            </div>
            <div className="p-5" style={{ minHeight: 400 }}>
              {renderOverview()}
            </div>
          </div>

          {/* ── Right sidebar: Notifications + Students ──────────────────────── */}
          <div className="flex flex-col gap-5">

            {/* Notifications */}
            <div className="rounded-2xl overflow-hidden flex flex-col"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', minHeight: 300 }}>
              <div className="flex items-center justify-between px-4 py-3 flex-shrink-0"
                style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex items-center gap-2">
                  <MdNotifications size={15} style={{ color: 'rgba(255,255,255,0.40)' }} />
                  <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.50)' }}>Notifications</span>
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
                    style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.35)' }}>
                    {notifications.length}
                  </span>
                </div>
                <button
                  onClick={() => {
                    setIsEditingNotification(false); setCurrentNotificationId(null);
                    setNewNotification({ title: '', content: '', date: new Date().toISOString().split('T')[0] });
                    setShowAddNotification(true);
                  }}
                  className="text-xs px-2.5 py-1 rounded-lg transition-all"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.45)' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = 'white'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; }}>
                  + Add
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ maxHeight: 320 }}>
                {notifications.length > 0 ? notifications.map((n) => (
                  <div key={n.id} className="rounded-xl p-3 relative overflow-hidden"
                    style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-xl" style={{ background: 'rgba(96,165,250,0.45)' }} />
                    <div className="pl-3 flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold mb-0.5 truncate" style={{ color: 'rgba(255,255,255,0.85)' }}>{n.title}</p>
                        <p className="text-[10px] mb-1" style={{ color: 'rgba(255,255,255,0.28)' }}>
                          {new Date(n.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                        <div className="text-xs line-clamp-2" style={{ color: 'rgba(255,255,255,0.45)' }}
                          dangerouslySetInnerHTML={{ __html: n.context }} />
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={() => handleEditNotification(n)} className="p-1 rounded transition-all"
                          style={{ color: 'rgba(96,165,250,0.55)' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(96,165,250,0.12)'; e.currentTarget.style.color = '#93c5fd'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(96,165,250,0.55)'; }}>
                          <MdEdit size={13} />
                        </button>
                        <button onClick={() => handleDeleteNotification(n.id)} className="p-1 rounded transition-all"
                          style={{ color: 'rgba(239,68,68,0.45)' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.12)'; e.currentTarget.style.color = '#f87171'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(239,68,68,0.45)'; }}>
                          <FaTrash size={10} />
                        </button>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="flex flex-col items-center justify-center h-24 text-center">
                    <MdNotifications className="text-3xl mb-1" style={{ color: 'rgba(255,255,255,0.08)' }} />
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>No notifications yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Students (compact) */}
            <div className="rounded-2xl overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex items-center justify-between px-4 py-3"
                style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex items-center gap-2">
                  <FaUserGraduate size={12} style={{ color: 'rgba(255,255,255,0.40)' }} />
                  <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.50)' }}>Students</span>
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
                    style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.35)' }}>
                    {table.length}
                  </span>
                </div>
                <button onClick={() => setIsAddStudentOpen(true)}
                  className="text-xs px-2.5 py-1 rounded-lg transition-all"
                  style={{ background: 'rgba(37,99,235,0.15)', border: '1px solid rgba(96,165,250,0.22)', color: '#93c5fd' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(37,99,235,0.28)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(37,99,235,0.15)'}>
                  + Add
                </button>
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: 200 }}>
                {table.length > 0 ? table.map((student, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5 border-t transition-colors"
                    style={{ borderColor: 'rgba(255,255,255,0.05)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.025)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <span className="text-xs font-medium truncate" style={{ color: 'rgba(255,255,255,0.75)' }}>
                      {student.first_name} {student.last_name}
                    </span>
                    <button onClick={() => handleRemoveStudent(student.u_id)}
                      className="text-[10px] px-2 py-0.5 rounded-md transition-all flex-shrink-0 ml-2"
                      style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.16)', color: 'rgba(239,68,68,0.60)' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.20)'; e.currentTarget.style.color = '#f87171'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; e.currentTarget.style.color = 'rgba(239,68,68,0.60)'; }}>
                      Remove
                    </button>
                  </div>
                )) : (
                  <div className="flex items-center justify-center py-8 text-center">
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.22)' }}>No students yet</p>
                  </div>
                )}
              </div>
            </div>

          </div>{/* end right sidebar */}
        </div>{/* end main grid */}

        {/* ── Assigned Homework ──────────────────────────────────────────────── */}
        <div className="rounded-2xl overflow-hidden"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <FaRegNoteSticky size={14} style={{ color: 'rgba(255,255,255,0.40)' }} />
            <span className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.60)' }}>Assigned Homework</span>
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
              style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.40)' }}>
              {filteredLength}
            </span>
          </div>
          <div className="p-4">
            {filteredLength > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {currentQuizzes.map((test) => {
                    const numq = JSON.parse(test.quiz_data)?.questions?.length;
                    const formattedDate = new Date(test.created_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    return (
                      <div key={test.testid} className="group relative rounded-2xl flex flex-col overflow-hidden transition-all duration-200"
                        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.13)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}>
                        <div className="h-[3px]" style={{ background: 'linear-gradient(90deg, #6366f1, #3b82f6)' }} />
                        <div className="flex-1 p-4">
                          <div className="flex items-center justify-between mb-3">
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
                              style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.28)', color: '#a5b4fc' }}>
                              {test.type}
                            </span>
                            <div className="flex items-center gap-1">
                              <button title="PDF" onClick={() => handlePrint(test)}
                                className="w-7 h-7 rounded-lg flex items-center justify-center transition-all"
                                style={{ color: 'rgba(255,255,255,0.28)' }}
                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.09)'; e.currentTarget.style.color = 'rgba(255,255,255,0.80)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.28)'; }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                              </button>
                              <button title="Setting"
                                onClick={() => { setsavedquizmodel(true); setquiztitle(test.name); setquizid(test.testid); setquizdata(ensureQuizDataStructure(test.quiz_data)); setquizgroup(test.quiz_group); setquiz_type(test.type); }}
                                className="w-7 h-7 rounded-lg flex items-center justify-center transition-all"
                                style={{ color: 'rgba(255,255,255,0.28)' }}
                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.09)'; e.currentTarget.style.color = 'rgba(255,255,255,0.80)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.28)'; }}>
                                <IoMdSettings size={14} />
                              </button>
                            </div>
                          </div>
                          <p className="text-sm font-semibold leading-snug line-clamp-2 mb-3" style={{ color: 'rgba(255,255,255,0.85)' }}>
                            {test.name}
                          </p>
                          <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.28)' }}>
                            {numq} questions · {formattedDate}
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-2 p-3 pt-0">
                          <button
                            onClick={() => { setquizview(true); setquiztitle(test.name); setquizid(test.testid); setquizdata(ensureQuizDataStructure(test.quiz_data)); setquizgroup(test.quiz_group); setquiz_type(test.type); get_quiz_view(test.testid); }}
                            className="py-2 rounded-xl text-xs font-bold transition-all"
                            style={{ background: 'rgba(99,102,241,0.18)', border: '1px solid rgba(99,102,241,0.30)', color: '#a5b4fc' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.32)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'rgba(99,102,241,0.18)'}>
                            View
                          </button>
                          <button
                            onClick={() => { setquiz_type(test.type); handleNavigate(test.testid); }}
                            className="py-2 rounded-xl text-xs font-bold transition-all"
                            style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.28)', color: '#6ee7b7' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(16,185,129,0.28)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'rgba(16,185,129,0.15)'}>
                            Resume
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {Math.ceil(filteredLength / quizzesPerPage) > 1 && (
                  <div className="flex justify-center gap-1.5 mt-4">
                    {Array.from({ length: Math.ceil(filteredLength / quizzesPerPage) }, (_, i) => (
                      <button key={i} onClick={() => paginate2(i + 1)}
                        className="w-8 h-8 rounded-lg text-sm font-semibold transition-all"
                        style={{
                          background: currentPage2 === i + 1 ? 'rgba(37,99,235,0.28)' : 'rgba(255,255,255,0.05)',
                          border: currentPage2 === i + 1 ? '1px solid rgba(96,165,250,0.40)' : '1px solid rgba(255,255,255,0.08)',
                          color: currentPage2 === i + 1 ? '#93c5fd' : 'rgba(255,255,255,0.40)',
                        }}>
                        {i + 1}
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-14 text-center">
                <FaRegFrownOpen className="text-3xl mb-3" style={{ color: 'rgba(255,255,255,0.15)' }} />
                <p className="text-sm font-semibold mb-1" style={{ color: 'rgba(255,255,255,0.40)' }}>No assignments yet</p>
                <p className="text-sm mb-5" style={{ color: 'rgba(255,255,255,0.22)' }}>Go to Saved Tests and assign a test to this class</p>
                <button onClick={() => setsection(8)}
                  className="px-4 py-2 rounded-xl text-sm font-semibold transition-all"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.50)' }}>
                  View Saved Tests
                </button>
              </div>
            )}
          </div>
        </div>

      </div>{/* end page wrapper */}

      {/* ── Notification modal ────────────────────────────────────────────────── */}
      <AnimatePresence>
        {showAddNotification && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
            style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(10px)' }}
            onClick={e => { if (e.target === e.currentTarget) setShowAddNotification(false); }}
          >
            <motion.div
              initial={{ opacity: 0, y: 14, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.97 }} transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="w-full max-w-2xl rounded-2xl overflow-hidden"
              style={{ background: '#12151f', border: '1px solid rgba(255,255,255,0.09)', boxShadow: '0 32px 64px rgba(0,0,0,0.6)' }}
            >
              <div className="flex items-center justify-between px-6 py-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                <h2 className="text-base font-bold text-white">
                  {isEditingNotification ? 'Edit Notification' : 'Add Notification'}
                </h2>
                <button onClick={() => setShowAddNotification(false)} className="p-1.5 rounded-lg transition-all"
                  style={{ color: 'rgba(255,255,255,0.30)' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = 'rgba(255,255,255,0.70)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.30)'; }}>
                  <MdClose size={16} />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: 'rgba(255,255,255,0.30)' }}>Date</label>
                    <input type="date" value={newNotification.date}
                      onChange={e => setNewNotification({ ...newNotification, date: e.target.value })}
                      className="w-full rounded-xl px-4 py-2.5 text-sm text-white outline-none transition-all"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', colorScheme: 'dark' }}
                      onFocus={e => e.target.style.border = '1px solid rgba(255,255,255,0.24)'}
                      onBlur={e => e.target.style.border = '1px solid rgba(255,255,255,0.09)'} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: 'rgba(255,255,255,0.30)' }}>Title</label>
                    <input type="text" value={newNotification.title}
                      onChange={e => setNewNotification({ ...newNotification, title: e.target.value })}
                      placeholder="Notification title…"
                      className="w-full rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 outline-none transition-all"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)' }}
                      onFocus={e => e.target.style.border = '1px solid rgba(255,255,255,0.24)'}
                      onBlur={e => e.target.style.border = '1px solid rgba(255,255,255,0.09)'} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: 'rgba(255,255,255,0.30)' }}>Content</label>
                  <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.09)' }}>
                    <div className="flex items-center gap-1 px-3 py-2" style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                      {[{ icon: FaBold, cmd: 'bold' }, { icon: FaItalic, cmd: 'italic' }, { icon: FaUnderline, cmd: 'underline' }].map(({ icon: Icon, cmd }) => (
                        <button key={cmd} type="button" onClick={() => handleFormatText(cmd)}
                          className="p-2 rounded-lg transition-all" style={{ color: 'rgba(255,255,255,0.45)' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'white'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; }}>
                          <Icon size={13} />
                        </button>
                      ))}
                    </div>
                    <div ref={editorRef} contentEditable onInput={handleEditorChange} onBlur={handleEditorChange}
                      className="min-h-[140px] p-4 text-sm focus:outline-none"
                      style={{ color: 'rgba(255,255,255,0.70)', background: 'rgba(255,255,255,0.02)' }}
                      dir="ltr" />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <button onClick={() => setShowAddNotification(false)}
                    className="px-4 py-2.5 rounded-xl text-sm font-medium transition-all"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.50)' }}>
                    Cancel
                  </button>
                  <button onClick={handleAddNotification}
                    className="px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all"
                    style={{ background: 'linear-gradient(135deg,#1d4ed8,#4f46e5)', boxShadow: '0 4px 16px rgba(29,78,216,0.30)' }}>
                    {isEditingNotification ? 'Update' : 'Add Notification'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <EditClassModal
        isEditOpen={isEditOpen}
        setIsEditOpen={setIsEditOpen}
        currentClass={currentClassData}
        handleEditClass={handleEditClass}
      />
      <AddStudentModal
        isOpen={isAddStudentOpen}
        onClose={() => setIsAddStudentOpen(false)}
        onAddStudent={handleAddStudent}
      />
    </>
  );
}

export default MyClassManagement;
